import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";

const ClientSelector = ({
  selectedClient,
  onClientChange,
  onError,
  className = "",
  disabled = false,
  autoSelectFirst = true,
  placeholder = "Select a client...",
  ...props
}) => {
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const { fetchWithAuth } = useAuth();

  const fetchClients = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/tables/clients/sample?limit=100&include_deleted=false`
      );
      if (response.ok) {
        const data = await response.json();
        // Transform the data from the API format to a more usable format
        const clientsList = data.rows.map((row) => {
          const clientData = {};
          data.columns.forEach((column, index) => {
            clientData[column] = row[index];
          });
          return clientData;
        });
        setClients(clientsList);

        // Auto-select first client if requested and no client is selected
        if (autoSelectFirst && clientsList.length > 0 && !selectedClient) {
          onClientChange(clientsList[0].client_id.toString());
        }
      } else {
        if (onError) {
          onError("Failed to fetch clients", "error");
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      if (onError) {
        onError("Failed to fetch clients", "error");
      }
    } finally {
      setIsLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    onClientChange(clientId);
  };

  const defaultClassName =
    "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed";

  return (
    <select
      value={selectedClient || ""}
      onChange={handleClientChange}
      disabled={disabled || isLoadingClients || clients.length === 0}
      className={className || defaultClassName}
      {...props}
    >
      {!selectedClient && !isLoadingClients && (
        <option value="">{placeholder}</option>
      )}
      {isLoadingClients ? (
        <option value="">Loading clients...</option>
      ) : clients.length === 0 ? (
        <option value="">No clients available</option>
      ) : (
        clients.map((client) => (
          <option key={client.client_id} value={client.client_id}>
            {client.name}
          </option>
        ))
      )}
    </select>
  );
};

export default ClientSelector;
